import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} [cartDiscountErrorClose] - The discount error close button element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {number | null} */
  #errorTimer = null;

  /** @type {string | null} */
  #baseDiscountErrorText = null;

  connectedCallback() {
    super.connectedCallback();
    const form = this.querySelector('.cart-discount__form');
    if (form) {
      form.removeEventListener('submit', this.applyDiscount);
      form.addEventListener('submit', this.applyDiscount);
    }
    const { cartDiscountError, cartDiscountErrorClose } = this.refs;
    if (cartDiscountErrorClose) {
      cartDiscountErrorClose.removeEventListener('click', this.clearError);
      cartDiscountErrorClose.addEventListener('click', this.clearError);
    }
    if (cartDiscountError) {
      cartDiscountError.removeEventListener('click', this.clearError);
      cartDiscountError.addEventListener('click', this.clearError);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#errorTimer) {
      clearTimeout(this.#errorTimer);
      this.#errorTimer = null;
    }
    const form = this.querySelector('.cart-discount__form');
    if (form) {
      form.removeEventListener('submit', this.applyDiscount);
    }
    const { cartDiscountError, cartDiscountErrorClose } = this.refs;
    if (cartDiscountErrorClose) {
      cartDiscountErrorClose.removeEventListener('click', this.clearError);
    }
    if (cartDiscountError) {
      cartDiscountError.removeEventListener('click', this.clearError);
    }
  }

  clearError = () => {
    if (this.#errorTimer) {
      clearTimeout(this.#errorTimer);
      this.#errorTimer = null;
    }
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    if (cartDiscountError) cartDiscountError.classList.add('hidden');
    if (cartDiscountErrorDiscountCode) cartDiscountErrorDiscountCode.classList.add('hidden');
    if (cartDiscountErrorShipping) cartDiscountErrorShipping.classList.add('hidden');
  };

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    const discountCode = this.querySelector('input[name="discount"]');
    const submitButton = this.querySelector('.cart-discount__button');
    const sectionId = this.dataset.sectionId || this.closest('[data-section-id]')?.dataset.sectionId;

    if (!(discountCode instanceof HTMLInputElement) || !sectionId) return;

    // Blur input immediately so iOS Safari keyboard dismisses cleanly before DOM mutations
    discountCode.blur();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    // Temporarily disable input to prevent double taps while fetch is in progress
    discountCode.disabled = true;
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.map((c) => c.toLowerCase()).includes(discountCodeValue.toLowerCase())) {
        discountCode.value = '';
        return;
      }

      if (this.#errorTimer) {
        clearTimeout(this.#errorTimer);
        this.#errorTimer = null;
      }
      if (cartDiscountError) cartDiscountError.classList.add('hidden');
      if (cartDiscountErrorDiscountCode) cartDiscountErrorDiscountCode.classList.add('hidden');
      if (cartDiscountErrorShipping) cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      // Wait 150ms to ensure iOS Safari soft keyboard slide-down animation has completely finished
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Check if discountCodeValue was accepted by Shopify
      const discountCodesInResponse = Array.isArray(data.discount_codes) ? data.discount_codes : null;
      let isApplicable = false;

      if (discountCodesInResponse) {
        const matchingDiscount = discountCodesInResponse.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code.toLowerCase() === discountCodeValue.toLowerCase();
        });
        isApplicable = !!matchingDiscount && matchingDiscount.applicable !== false;
      } else {
        const newHtml = data.sections?.[sectionId];
        if (newHtml) {
          const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
          const discountPills = parsedHtml.querySelectorAll('.cart-discount__pill');
          const codes = Array.from(discountPills)
            .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
            .filter(Boolean);
          isApplicable = codes.map((c) => c.toLowerCase()).includes(discountCodeValue.toLowerCase());
        }
      }

      if (!isApplicable) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code', discountCodeValue);
        return;
      }

      const newHtml = data.sections[sectionId];
      if (!newHtml) return;

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const discountCodes = parsedHtml.querySelectorAll('.cart-discount__pill');
      const codes = Array.from(discountCodes)
        .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
        .filter(Boolean);

      // Before morphing, check if shipping discount was applicable
      if (
        codes.length === existingDiscounts.length &&
        codes.every((/** @type {string} */ code) => existingDiscounts.includes(code))
      ) {
        this.#handleDiscountError('shipping', discountCodeValue);
        discountCode.value = '';
        return;
      }

      discountCode.value = '';
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      const cartItemsComponent = this.closest('cart-items-component');
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

      // Only morph directly if cart-items-component isn't already handling it via DiscountUpdateEvent
      if (!cartItemsComponent && newHtml) {
        const isDrawer = this.closest('cart-drawer-component') !== null;
        morphSection(sectionId, newHtml, isDrawer ? 'hydration' : 'full', { injectStylesheet: true });
      }

      // Force WebKit scroll container reflow for iOS Safari
      const dialog = this.closest('cart-drawer-component')?.querySelector('dialog');
      if (dialog) {
        dialog.scrollTop = dialog.scrollTop;
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
    } finally {
      discountCode.disabled = false;
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
      this.#activeFetch = null;
      if (event) {
        cartPerformance.measureFromEvent('discount-update:user-action', event);
      }
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const sectionId = this.dataset.sectionId || this.closest('[data-section-id]')?.dataset.sectionId;

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !sectionId
    ) {
      return;
    }

    const targetElement = event?.target instanceof Element ? event.target : this;
    const pill = targetElement.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      // Wait 100ms for iOS Safari animations
      await new Promise((resolve) => setTimeout(resolve, 100));

      const cartItemsComponent = this.closest('cart-items-component');
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

      if (!cartItemsComponent && data.sections?.[sectionId]) {
        const isDrawer = this.closest('cart-drawer-component') !== null;
        morphSection(sectionId, data.sections[sectionId], isDrawer ? 'hydration' : 'full', { injectStylesheet: true });
      }

      // Force WebKit scroll container reflow for iOS Safari
      const dialog = this.closest('cart-drawer-component')?.querySelector('dialog');
      if (dialog) {
        dialog.scrollTop = dialog.scrollTop;
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error(error);
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   * @param {string} [codeValue=''] - The code value entered by the user.
   */
  #handleDiscountError(type, codeValue = '') {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;

    if (this.#errorTimer) {
      clearTimeout(this.#errorTimer);
      this.#errorTimer = null;
    }

    if (type === 'discount_code' && cartDiscountErrorDiscountCode) {
      if (!this.#baseDiscountErrorText) {
        this.#baseDiscountErrorText = cartDiscountErrorDiscountCode.textContent?.trim() || '';
      }
      if (codeValue) {
        if (/['"].*?['"]|test/i.test(this.#baseDiscountErrorText)) {
          cartDiscountErrorDiscountCode.textContent = this.#baseDiscountErrorText.replace(
            /['"].*?['"]|test/i,
            () => `'${codeValue}'`
          );
        } else {
          cartDiscountErrorDiscountCode.textContent = `'${codeValue}': ${this.#baseDiscountErrorText}`;
        }
      } else {
        cartDiscountErrorDiscountCode.textContent = this.#baseDiscountErrorText;
      }
    }
    if (cartDiscountError) cartDiscountError.classList.remove('hidden');
    if (target) target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}


